import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import PartnerDashboard from './components/PartnerDashboard';
import DepartmentDashboard from './components/DepartmentDashboard';
import ResponsesPage from './components/ResponsesPage';
import Login from './components/Login';
import RampMEDashboard from './components/RampMEDashboard';
import RampMEDashboardPublic from './components/RampMEDashboardPublic';
import { FilterOptions } from './types';
import apiClient from './utils/api';
import { PartnersProvider } from './contexts/PartnersContext';
import { CacheProvider, useCache } from './contexts/CacheContext';

// Inner App component that uses the cache context
function AppContent() {
  // Check for token in URL synchronously before first render to avoid race condition
  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromUrl = urlParams.get('token');

  // Initialize authentication state synchronously
  const getInitialAuthState = (): boolean => {
    // First priority: token from URL
    if (tokenFromUrl) {
      // Store token in localStorage immediately
      localStorage.setItem('token', tokenFromUrl);
      localStorage.setItem('isAuthenticated', 'true');

      // Remove token from URL without page reload
      urlParams.delete('token');
      const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
      window.history.replaceState({}, '', newUrl);

      return true;
    }

    // Second priority: check localStorage
    const authStatus = localStorage.getItem('isAuthenticated');
    const storedToken = localStorage.getItem('token');

    return authStatus === 'true' && !!storedToken;
  };

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(getInitialAuthState());
  const [filters, setFilters] = useState<FilterOptions>({
    workshops: [],
    districts: [],
    implementationPartners: [],
    dateRange: { start: '', end: '' }
  });
  const { invalidateCache } = useCache();

  // Handle token processing and cache invalidation after initial render
  useEffect(() => {
    if (tokenFromUrl) {
      // Token was already processed synchronously, now invalidate cache
      invalidateCache();
      window.dispatchEvent(new Event('auth-changed'));
      window.dispatchEvent(new CustomEvent('cache-invalidated', {
        detail: { timestamp: Date.now() }
      }));
    } else {
      // Check if already authenticated via localStorage (fallback)
      const authStatus = localStorage.getItem('isAuthenticated');
      const storedToken = localStorage.getItem('token');

      if (authStatus === 'true' && storedToken && !isAuthenticated) {
        setIsAuthenticated(true);
      }
    }
  }, [tokenFromUrl, invalidateCache, isAuthenticated]);

  const handleLogin = (authenticated: boolean) => {
    setIsAuthenticated(authenticated);
    if (authenticated) {
      // Invalidate cache on login to ensure fresh data
      invalidateCache();
      // Notify PartnersContext to refetch data
      window.dispatchEvent(new Event('auth-changed'));
      // Trigger cache invalidation event
      window.dispatchEvent(new CustomEvent('cache-invalidated', {
        detail: { timestamp: Date.now() }
      }));
    }
  };

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint to invalidate token
      await apiClient.logout();
    } catch (error) {
      // Even if logout API call fails, continue with local cleanup
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local storage and update state
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setIsAuthenticated(false);
      // Notify PartnersContext to clear data
      window.dispatchEvent(new Event('auth-changed'));
    }
  };

  return (
    <PartnersProvider>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <Routes>
            {/* Login Route */}
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <Navigate to="/ramp-me" replace />
                ) : (
                  <Login onLogin={handleLogin} />
                )
              }
            />

            {/* Protected Routes */}
            <Route
              path="/ramp-me"
              element={
                isAuthenticated ? (
                  <RampMEDashboard onLogout={handleLogout} />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Public Routes */}
            <Route path="/ramp-me-public" element={<RampMEDashboardPublic />} />
            <Route
              path="/feedback-dashboard"
              element={
                isAuthenticated ? (
                  <Dashboard
                    filters={filters}
                    onFiltersChange={setFilters}
                    onLogout={handleLogout}
                  />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/partners"
              element={
                isAuthenticated ? (
                  <PartnerDashboard
                    filters={filters}
                    onFiltersChange={setFilters}
                    onLogout={handleLogout}
                  />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/departments"
              element={
                isAuthenticated ? (
                  <DepartmentDashboard
                    filters={filters}
                    onFiltersChange={setFilters}
                    onLogout={handleLogout}
                  />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/responses"
              element={
                isAuthenticated ? (
                  <ResponsesPage
                    filters={filters}
                    onLogout={handleLogout}
                  />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Default redirect */}
            <Route
              path="/"
              element={<Navigate to={isAuthenticated ? '/ramp-me' : '/login'} replace />}
            />
          </Routes>
        </div>
      </Router>
    </PartnersProvider>
  );
}

// Main App component wrapped with providers
function App() {
  return (
    <CacheProvider>
      <AppContent />
    </CacheProvider>
  );
}

export default App;